function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function formatJsValue(value, indent) {
  const pad = ' '.repeat(indent)
  const inner = ' '.repeat(indent + 2)

  if (value === null) return 'null'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return JSON.stringify(value)

  if (Array.isArray(value)) {
    if (!value.length) return '[]'
    const items = value.map(
      (item) => `${inner}${formatJsValue(item, indent + 2)}`,
    )
    return `[\n${items.join(',\n')}\n${pad}]`
  }

  const entries = Object.entries(value)
  if (!entries.length) return '{}'

  const props = entries.map(([key, nested]) => {
    const safeKey = /^[a-zA-Z_$][\w$]*$/.test(key) ? key : JSON.stringify(key)
    return `${inner}${safeKey}: ${formatJsValue(nested, indent + 2)}`
  })

  return `{\n${props.join(',\n')}\n${pad}}`
}

function formatPythonValue(value, indent) {
  const pad = ' '.repeat(indent)
  const inner = ' '.repeat(indent + 4)

  if (value === null) return 'None'
  if (typeof value === 'boolean') return value ? 'True' : 'False'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') {
    return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
  }

  if (Array.isArray(value)) {
    if (!value.length) return '[]'
    const items = value.map(
      (item) => `${inner}${formatPythonValue(item, indent + 4)}`,
    )
    return `[\n${items.join(',\n')}\n${pad}]`
  }

  const entries = Object.entries(value)
  if (!entries.length) return '{}'

  const props = entries.map(([key, nested]) => {
    return `${inner}'${key.replace(/'/g, "\\'")}': ${formatPythonValue(nested, indent + 4)}`
  })

  return `{\n${props.join(',\n')}\n${pad}}`
}

function indentBlock(text, spaces) {
  const pad = ' '.repeat(spaces)
  return text
    .split('\n')
    .map((line, index) => (index === 0 ? line : `${pad}${line}`))
    .join('\n')
}

export function buildTestRequestBody(requestBody, apiKey = '') {
  try {
    const parsed = JSON.parse(requestBody)
    if (apiKey && parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      parsed.api_key = apiKey
    }
    return JSON.stringify(parsed, null, 2)
  } catch {
    return String(requestBody ?? '').trim()
  }
}

export function buildEditableTestRequestBody(requestBody) {
  try {
    const parsed = JSON.parse(requestBody)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      delete parsed.api_key
    }
    return JSON.stringify(parsed, null, 2)
  } catch {
    return String(requestBody ?? '').trim()
  }
}

export function buildTestRequestPayload(bodyText, apiKey = '') {
  const parsed = JSON.parse(bodyText)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Request body must be a JSON object.')
  }
  if (apiKey) {
    parsed.api_key = apiKey
  }
  return parsed
}

export function isInlineResponseEndpoint(endpoint) {
  if (!endpoint) return false

  const path = String(endpoint.path ?? '').toLowerCase()
  const id = String(endpoint.id ?? '').toLowerCase()

  if (path.endsWith('/list') || id.endsWith('-list')) return true
  if (path.includes('/get') || id.endsWith('-get')) return true

  return false
}

export async function executeYandleApiRequest(baseUrl, path, payload) {
  const root = String(baseUrl ?? '').replace(/\/$/, '')
  const route = String(path ?? '').startsWith('/') ? path : `/${path}`
  const url = `${root}${route}`
  const startedAt = performance.now()

  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  const elapsedMs = Math.round(performance.now() - startedAt)
  const raw = await response.text()
  let body = raw

  try {
    body = raw ? JSON.parse(raw) : null
  } catch {
    body = raw
  }

  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    elapsedMs,
    body,
    raw,
  }
}

export function buildNodeExample(baseUrl, path, requestBody) {
  let payload
  try {
    payload = JSON.parse(requestBody)
  } catch {
    return requestBody
  }

  const body = formatJsValue(payload, 2).replace(/\n/g, '\n  ')
  return `const response = await fetch('${baseUrl}${path}', {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(${body})
});

const data = await response.json();`
}

export function buildPythonExample(baseUrl, path, requestBody) {
  let payload
  try {
    payload = JSON.parse(requestBody)
  } catch {
    return requestBody
  }

  const body = indentBlock(formatPythonValue(payload, 4), 4)
  return `import requests

response = requests.put(
    '${baseUrl}${path}',
    json=${body}
)
data = response.json()`
}

export function highlightJson(code) {
  return highlightCodeTokens(code, 'json')
}

export function highlightNode(code) {
  return highlightCodeTokens(code, 'node')
}

export function highlightPython(code) {
  return highlightCodeTokens(code, 'python')
}

function highlightCodeTokens(code, language) {
  return String(code)
    .split('\n')
    .map((line) => highlightLine(line, language))
    .join('\n')
}

function highlightLine(line, language) {
  const parts = []
  let index = 0

  while (index < line.length) {
    const rest = line.slice(index)

    if (rest.startsWith('/*')) {
      const end = rest.indexOf('*/')
      const comment = end === -1 ? rest : rest.slice(0, end + 2)
      parts.push(`<span class="gray">${escapeHtml(comment)}</span>`)
      index += comment.length
      continue
    }

    if (language !== 'json') {
      const stringMatch = rest.match(/^('([^'\\]|\\.)*'|"([^"\\]|\\.)*")/)
      if (stringMatch) {
        parts.push(`<span class="str">${stringMatch[0]}</span>`)
        index += stringMatch[0].length
        continue
      }
    }

    if (language === 'json') {
      const stringMatch = rest.match(/^"([^"\\]|\\.)*"/)
      if (stringMatch) {
        const token = stringMatch[0]
        const next = rest.slice(token.length)
        if (next.startsWith(':')) {
          parts.push(`<span class="kw">${escapeHtml(token)}</span>:`)
          index += token.length + 1
        } else {
          parts.push(`<span class="str">${escapeHtml(token)}</span>`)
          index += token.length
        }
        continue
      }
    }

    const keywordPattern =
      language === 'node'
        ? /^(\bconst\b|\bawait\b|\bfetch\b|\bmethod\b|\bheaders\b|\bbody\b|\bJSON\b|\bstringify\b)/
        : language === 'python'
          ? /^(\bimport\b|\brequests\b|\bput\b|\bjson\b|\bTrue\b|\bFalse\b|\bNone\b)/
          : null

    if (keywordPattern) {
      const keywordMatch = rest.match(keywordPattern)
      if (keywordMatch) {
        parts.push(`<span class="kw">${keywordMatch[0]}</span>`)
        index += keywordMatch[0].length
        continue
      }
    }

    const literalMatch = rest.match(
      /^(\btrue\b|\bfalse\b|\bnull\b|\bTrue\b|\bFalse\b|\bNone\b|-?\d+(?:\.\d*)?)/,
    )
    if (literalMatch) {
      parts.push(`<span class="green">${literalMatch[0]}</span>`)
      index += literalMatch[0].length
      continue
    }

    const punctMatch = rest.match(/^[{}[\],():.]/)
    if (punctMatch) {
      parts.push(`<span class="gray">${punctMatch[0]}</span>`)
      index += 1
      continue
    }

    parts.push(escapeHtml(rest[0]))
    index += 1
  }

  return parts.join('')
}
